import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// Module-level literals — schema extractor follows one level (same shape as PropertyInfoPanel).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone',
  fields: {
    text: {
      label: 'Phone',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/**
 * Schedule a Tour — tour scheduler + property info tiles.
 * Same layout/block wiring as ContactUs (Preferred Theme pattern).
 */
export function ScheduleTourForm() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const formSlot = useBlockSlot('scheduletourform.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up stf-section">
      <div className="stf-container">
        <div className="stf-grid">
          <div className="stf-form-col">
            <EditableToken path="scheduletourform.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : null}
            </EditableToken>
          </div>

          <div className="stf-info-col">
            <PropertyInfoPanel prefix="scheduletourform" />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — PropertyInfoPanel uses dynamic `${prefix}.*` paths. */}
      <EditableToken path="scheduletourform.visit_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.call_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup groupPath="scheduletourform.address" definition={addressLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="scheduletourform.phone" definition={phoneLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableToken path="scheduletourform.hours_label" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .stf-section {
          padding: 2.5rem 0 3.5rem;
          background: var(--vibe-color-background);
        }
        @media (min-width: 768px) {
          .stf-section { padding: 3rem 0 4rem; }
        }
        .stf-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .stf-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .stf-container { padding: 0 2rem; } }

        .stf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .stf-grid {
            grid-template-columns: 7fr 5fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        .stf-info-col .cf-info-cards {
          flex-direction: column;
          flex-wrap: nowrap;
          gap: 0.75rem;
        }
        .stf-info-col .cf-card {
          flex: none;
          width: 100%;
        }

        @media (max-width: 1023px) {
          .stf-info-col .cf-info-cards {
            flex-direction: row;
            flex-wrap: wrap;
          }
          .stf-info-col .cf-card {
            flex: 1 1 140px;
          }
        }

        .stf-form-col { position: relative; }
        .stf-form-col h3 {
          text-transform: uppercase;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Tour Scheduler Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduletourform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduletourform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': addressLinkDef,
    'scheduletourform.phone': phoneLinkDef,
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
